// frontend/src/data/shopData.js
// Shop V1 — Mock seed data for PR6 shop-state endpoints.
// All data here is MOCK — pending PR6 backend.
// This module is imported by shopApi.js to seed the mutable mock state.

// ---------------------------------------------------------------------------
// Pull Ladder — cost per paid pull (daily free pull does NOT advance this)
// ---------------------------------------------------------------------------

/** @type {number[]} Index 0 = pull #1 cost, index 7 = pull #8 cost (capped) */
export const PULL_LADDER = [
    0,    // Pull 1: FREE (daily or punch credit — handled by entitlement logic)
    25,   // Pull 2: 25 Mythril
    40,   // Pull 3: 40 Mythril
    65,   // Pull 4: 65 Mythril
    100,  // Pull 5: 100 Mythril
    160,  // Pull 6: 160 Mythril
    250,  // Pull 7: 250 Mythril
    400,  // Pull 8: 400 Mythril (capped — printer locks after this)
];

// ---------------------------------------------------------------------------
// Variant Pack cost
// ---------------------------------------------------------------------------

export const VARIANT_PACK_COST = 200; // Mythril

// ---------------------------------------------------------------------------
// Initial mock shop state
// ---------------------------------------------------------------------------

/**
 * MOCK — pending PR6 backend
 * Seed state for getShopState(). shopApi.js copies this into its mutable
 * _mockState object so resets work without a page reload.
 *
 * NOTE: mythrilBalance is intentionally NOT tracked here — it is owned by
 * AuthContext (AuthProvider.js). The mock state only tracks pull/punch state.
 *
 * @type {{
 *   punchCount: number,
 *   dailyFreeConsumed: boolean,
 *   punchCredits: number,
 *   currentPullTier: number,
 *   hasFreeAvailable: boolean,
 *   pullsUsedToday: number,
 *   printerLocked: boolean,
 * }}
 */
export const INITIAL_MOCK_SHOP_STATE = {
    // mythrilBalance removed — Fix 7A: balance is owned by AuthContext, not mock state
    punchCount: 2,           // 0–4 punch holes filled
    dailyFreeConsumed: false,
    punchCredits: 0,         // earned free pulls from punch card
    currentPullTier: 1,      // 1–8, maps to PULL_LADDER index (1-based)
    hasFreeAvailable: true,  // dailyFreeConsumed===false OR punchCredits>0
    pullsUsedToday: 0,       // paid pulls used today (0–7; 8 = printer locked)
    printerLocked: false,
};

// ---------------------------------------------------------------------------
// Mock card pool — used by openShopOffer() to generate results
// ---------------------------------------------------------------------------

/**
 * MOCK — pending PR6 backend
 * Sample cards returned by the printer and variant pack.
 * Each card shape matches the planned PR6 backend outcome contract.
 */
// Fix 8: theme_key values updated to match flat-folder resolver in shopApi.js.
// 'cartoon' → player_images_gsis_cartoon/
// 'variant' → player_images_gsis_variants/
// 'legacy'  → player_images_gsis/
export const MOCK_CARD_POOL = [
    {
        ownership_id: 'mock-own-001',
        gsis_id: '00-0023459',
        player_name: 'Patrick Mahomes',
        position: 'QB',
        team: 'KC',
        theme_key: 'cartoon',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-002',
        gsis_id: '00-0031408',
        player_name: 'Justin Jefferson',
        position: 'WR',
        team: 'MIN',
        theme_key: 'variant',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-003',
        gsis_id: '00-0036442',
        player_name: "Ja'Marr Chase",
        position: 'WR',
        team: 'CIN',
        theme_key: 'cartoon',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-004',
        gsis_id: '00-0034857',
        player_name: 'Tyreek Hill',
        position: 'WR',
        team: 'MIA',
        theme_key: 'variant',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-005',
        gsis_id: '00-0033873',
        player_name: 'Davante Adams',
        position: 'WR',
        team: 'LV',
        theme_key: 'legacy',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-006',
        gsis_id: '00-0029604',
        player_name: 'Christian McCaffrey',
        position: 'RB',
        team: 'SF',
        theme_key: 'cartoon',
        is_duplicate: false,
    },
    {
        ownership_id: 'mock-own-007',
        gsis_id: '00-0038520',
        player_name: 'Bijan Robinson',
        position: 'RB',
        team: 'ATL',
        theme_key: 'variant',
        is_duplicate: false,
    },
];

// ---------------------------------------------------------------------------
// Mythril IAP tiers (V1 display-only — no real payment)
// ---------------------------------------------------------------------------

/**
 * Illustrative IAP tiers per ShopV1-Design-Document.md §6.
 * V1 is display-only; clicking a tier fires a mock callback.
 * Real fiat purchasing is post-launch.
 */
export const MYTHRIL_TIERS = [
    { id: 1, usd: '$1.00',  mythril: 100,  bonus: null },
    { id: 2, usd: '$5.00',  mythril: 600,  bonus: '+20% bonus' },
    { id: 3, usd: '$10.00', mythril: 1500, bonus: '+50% bonus' },
    { id: 4, usd: '$20.00', mythril: 3500, bonus: '+75% bonus' },
];
